/**
 * Page /admin/audit — journal d'audit des actions admin (lecture seule, paginée).
 * Alimentée par GET /api/admin/audit (offset/limit + total).
 */
import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ScrollText, Loader2, ChevronLeft, ChevronRight, ShieldAlert, ChevronDown, Undo2, RotateCcw } from "lucide-react";
import { useConfirm } from "@/components/admin/ConfirmProvider";

function _extractError(e) {
    const d = e?.response?.data?.detail;
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" · ");
    if (d && typeof d === "object") return d.msg || JSON.stringify(d);
    return e?.message || "Erreur inconnue";
}

const ACTION_LABELS = {
    "user.create": "Création utilisateur",
    "user.update": "Modification utilisateur",
    "user.delete": "Suppression utilisateur",
    "content.update": "Édition du contenu",
    "content.upload": "Upload image",
    "mail.send": "Envoi d'email",
    "mail.settings": "Réglages mail",
    "2fa.enable": "Activation 2FA",
    "2fa.disable": "Désactivation 2FA",
};

const PAGE = 50;

function fmt(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("fr-FR", {
            day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
        });
    } catch { return iso; }
}

export default function AdminAudit() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [offset, setOffset] = useState(0);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(null);
    const [reverting, setReverting] = useState(null);
    const confirm = useConfirm();

    async function revert(entry) {
        if (!(await confirm({ title: "Rétablir cette modification ?", message: "Le contenu actuel sera remplacé par les valeurs précédentes.", confirmLabel: "Rétablir", danger: false }))) return;
        setReverting(entry.id);
        try {
            await api.post(`/admin/audit/${entry.id}/revert`);
            toast.success("Contenu rétabli");
            load();
        } catch (e) {
            toast.error("Échec : " + _extractError(e));
        } finally {
            setReverting(null);
        }
    }

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/audit?limit=${PAGE}&offset=${offset}`);
            setItems(data.items || []);
            setTotal(data.total || 0);
        } catch (e) {
            toast.error("Chargement : " + _extractError(e));
        } finally {
            setLoading(false);
        }
    }, [offset]);

    useEffect(() => { load(); }, [load]);

    const from = total === 0 ? 0 : offset + 1;
    const to = Math.min(offset + PAGE, total);

    return (
        <div className="em-container py-8">
            <p className="em-eyebrow">Sécurité</p>
            <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight text-primary mt-2 flex items-center gap-3">
                <ScrollText className="h-9 w-9" /> Journal d'audit
            </h1>
            <p className="text-muted-foreground mt-3 max-w-2xl">
                Trace horodatée des actions sensibles réalisées dans l'admin (utilisateurs, contenu,
                emails, réglages, 2FA). Lecture seule.
            </p>

            {loading ? (
                <div className="flex items-center gap-2 text-muted-foreground py-20 justify-center">
                    <Loader2 className="h-5 w-5 animate-spin" /> Chargement…
                </div>
            ) : items.length === 0 ? (
                <div className="rounded-3xl border border-border bg-card p-12 mt-8 text-center text-muted-foreground">
                    <ShieldAlert className="h-8 w-8 mx-auto mb-3 opacity-50" />
                    Aucune action enregistrée pour l'instant.
                </div>
            ) : (
                <>
                    <div className="rounded-2xl border border-border overflow-hidden mt-8">
                        <table className="w-full text-sm">
                            <thead className="bg-secondary text-left">
                                <tr>
                                    <th className="px-4 py-3 font-semibold w-8"></th>
                                    <th className="px-4 py-3 font-semibold">Date</th>
                                    <th className="px-4 py-3 font-semibold">Auteur</th>
                                    <th className="px-4 py-3 font-semibold">Action</th>
                                    <th className="px-4 py-3 font-semibold">Détail</th>
                                    <th className="px-4 py-3 font-semibold hidden md:table-cell">IP</th>
                                    <th className="px-4 py-3 font-semibold text-right">Annuler</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((it) => {
                                    const hasChanges = Array.isArray(it.changes) && it.changes.length > 0;
                                    const open = expanded === it.id;
                                    return (
                                        <React.Fragment key={it.id}>
                                            <tr
                                                className={`border-t border-border ${hasChanges ? "cursor-pointer hover:bg-secondary/40" : ""}`}
                                                onClick={() => hasChanges && setExpanded(open ? null : it.id)}
                                            >
                                                <td className="px-4 py-3 text-muted-foreground">
                                                    {hasChanges && <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />}
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">{fmt(it.at)}</td>
                                                <td className="px-4 py-3 whitespace-nowrap">{it.actor_email || "—"}</td>
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <span className="inline-block rounded-full bg-primary/10 text-primary px-2.5 py-0.5 text-xs font-semibold">
                                                        {ACTION_LABELS[it.action] || it.action}
                                                    </span>
                                                    {it.reverted && <span className="ml-2 text-xs text-muted-foreground italic">annulé</span>}
                                                </td>
                                                <td className="px-4 py-3">{it.summary || "—"}</td>
                                                <td className="px-4 py-3 whitespace-nowrap text-muted-foreground hidden md:table-cell font-mono text-xs">{it.ip || "—"}</td>
                                                <td className="px-4 py-3 text-right">
                                                    {it.revertable ? (
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); revert(it); }}
                                                            disabled={reverting === it.id}
                                                            className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-semibold hover:bg-secondary disabled:opacity-40"
                                                            title="Rétablir les valeurs précédentes"
                                                        >
                                                            {reverting === it.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Undo2 className="h-3.5 w-3.5" />}
                                                            Rétablir
                                                        </button>
                                                    ) : (
                                                        <span className="text-xs text-muted-foreground/50">—</span>
                                                    )}
                                                </td>
                                            </tr>
                                            {open && hasChanges && (
                                                <tr className="border-t border-border bg-secondary/20">
                                                    <td></td>
                                                    <td colSpan={6} className="px-4 py-4">
                                                        <div className="space-y-3">
                                                            {it.changes.map((ch, i) => (
                                                                <div key={i} className="rounded-lg border border-border bg-card p-3">
                                                                    <p className="text-xs font-mono font-semibold text-primary mb-2">{ch.key}</p>
                                                                    <div className="grid md:grid-cols-2 gap-3">
                                                                        <div>
                                                                            <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Avant</p>
                                                                            <p className="text-sm bg-destructive/5 border border-destructive/20 rounded p-2 whitespace-pre-wrap break-words">{ch.before || <span className="italic text-muted-foreground">(vide)</span>}</p>
                                                                        </div>
                                                                        <div>
                                                                            <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Après</p>
                                                                            <p className="text-sm bg-primary/5 border border-primary/20 rounded p-2 whitespace-pre-wrap break-words">{ch.after || <span className="italic text-muted-foreground">(vide)</span>}</p>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="flex items-center justify-between mt-4">
                        <span className="text-sm text-muted-foreground">{from}–{to} sur {total}</span>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setOffset(Math.max(0, offset - PAGE))}
                                disabled={offset === 0}
                                className="inline-flex items-center gap-1 rounded-full border border-border px-4 py-2 text-sm font-semibold hover:bg-secondary disabled:opacity-40"
                            >
                                <ChevronLeft className="h-4 w-4" /> Précédent
                            </button>
                            <button
                                onClick={() => setOffset(offset + PAGE)}
                                disabled={to >= total}
                                className="inline-flex items-center gap-1 rounded-full border border-border px-4 py-2 text-sm font-semibold hover:bg-secondary disabled:opacity-40"
                            >
                                Suivant <ChevronRight className="h-4 w-4" />
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
